import { useCallback, useEffect, useState } from "react";
import { fetchDealerDetail } from "../../../../services/dealerCoverageApi";
import { updateDealerProfile } from "../../../../services/dealerProfileControls";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import AdminTableContainer from "../layout/AdminTableContainer";

const DealerDetailAdminPage = ({ profileKey, onClose }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [noteDraft, setNoteDraft] = useState("");

  const load = useCallback(async () => {
    if (!profileKey) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchDealerDetail(profileKey);
      setDetail(data.detail);
      setNoteDraft(data.detail?.notes || "");
    } catch (err) {
      setErrorMessage(err.message || "Failed to load dealer detail");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [profileKey]);

  useEffect(() => {
    load();
  }, [load]);

  const patchStatus = async (patch) => {
    if (!canManage || !profileKey) return;
    try {
      await updateDealerProfile(profileKey, patch);
      await load();
    } catch (err) {
      setErrorMessage(err.message || "Update failed");
    }
  };

  if (!profileKey) {
    return <p className="admin-error">No dealer profile key provided.</p>;
  }

  const profile = detail?.profile;
  const eligibility = detail?.goalEligibility;

  return (
    <div className="admin-page automotive-dealer-detail-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Automotive · Dealer Registry</p>
          <h1>{profile?.displayName || profileKey}</h1>
          <p className="muted">{profile?.canonicalName}</p>
        </div>
        <div className="admin-page__actions">
          <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealers">
            Back to list
          </a>
          <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealer-coverage">
            Coverage dashboard
          </a>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {loading ? <p className="muted">Loading…</p> : null}
      {errorMessage ? <p className="admin-error">{errorMessage}</p> : null}

      {profile ? (
        <>
          <section className="panel-card">
            <h3>Profile summary</h3>
            <dl className="admin-dl-grid">
              <dt>Profile key</dt>
              <dd>{profile.profileKey}</dd>
              <dt>City</dt>
              <dd>{profile.primaryCity || "—"} ({profile.department || "Antioquia"})</dd>
              <dt>Dealer group</dt>
              <dd>{profile.dealerGroupName || "—"}</dd>
              <dt>Branch</dt>
              <dd>{profile.isBranch ? profile.branchName || "Yes" : "—"}</dd>
              <dt>Verification</dt>
              <dd>{profile.verificationStatus}</dd>
              <dt>Ingest status</dt>
              <dd>{profile.ingestStatus}</dd>
              <dt>Readiness tier</dt>
              <dd>{profile.readinessTier || "—"}</dd>
              <dt>Counts toward goal</dt>
              <dd>{eligibility?.countsTowardGoal ? "Yes" : `No — ${eligibility?.excludedReason || "excluded"}`}</dd>
              <dt>Source type</dt>
              <dd>{eligibility?.primarySourceType || "—"}</dd>
              <dt>Completeness</dt>
              <dd>
                {detail.completeness?.completenessScore ?? "—"}
                {detail.completeness?.missingFields?.length
                  ? ` (missing: ${detail.completeness.missingFields.join(", ")})`
                  : ""}
              </dd>
              <dt>Freshness</dt>
              <dd>
                {detail.freshness?.sourceHealthStatus || "—"}
                {detail.freshness?.staleFlag ? " (stale)" : ""}
              </dd>
              <dt>Health score</dt>
              <dd>
                {detail.health?.dealerHealthScore ?? profile.dealerHealthScore ?? "—"}
                {detail.health?.dealerHealthBand || profile.dealerHealthBand
                  ? ` (${detail.health?.dealerHealthBand || profile.dealerHealthBand})`
                  : ""}
              </dd>
              <dt>Open governance findings</dt>
              <dd>
                {detail.health?.openGovernanceFindings ?? 0}
                {detail.health?.criticalGovernanceFindings
                  ? ` (${detail.health.criticalGovernanceFindings} critical)`
                  : ""}
              </dd>
              {detail.health?.dealerHealthReasons?.length ? (
                <>
                  <dt>Health reasons</dt>
                  <dd>{detail.health.dealerHealthReasons.join(", ")}</dd>
                </>
              ) : null}
            </dl>
            {canManage ? (
              <div className="admin-page__toolbar" style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button type="button" className="admin-btn admin-btn--compact" onClick={() => patchStatus({ verificationStatus: "verified" })}>
                  Mark verified
                </button>
                <button type="button" className="admin-btn admin-btn--compact" onClick={() => patchStatus({ verificationStatus: "needs_review" })}>
                  Mark needs review
                </button>
                <button type="button" className="admin-btn admin-btn--compact" onClick={() => patchStatus({ verificationStatus: "duplicate_candidate" })}>
                  Mark duplicate candidate
                </button>
                <button type="button" className="admin-btn admin-btn--compact" onClick={() => patchStatus({ verificationStatus: "deferred" })}>
                  Mark deferred
                </button>
              </div>
            ) : null}
          </section>

          {detail.sourcePresences?.length ? (
            <AdminTableContainer title="Source presences">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Listings seen</th>
                    <th>Last seen</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.sourcePresences.map((p) => (
                    <tr key={p.id}>
                      <td>{p.presenceType}</td>
                      <td>{p.sourceKey || p.sourceName || p.marketplaceName || "—"}</td>
                      <td>{p.listingCountSeen ?? "—"}</td>
                      <td>{p.lastSeenAt ? new Date(p.lastSeenAt).toLocaleDateString() : "—"}</td>
                      <td>{p.verificationStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : null}

          {detail.inventorySources?.length ? (
            <AdminTableContainer title="Inventory sources">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Automotive source</th>
                    <th>Readiness</th>
                    <th>Ingestion</th>
                    <th>Last success</th>
                    <th>Active listings</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.inventorySources.map((s) => (
                    <tr key={s.id}>
                      <td>{s.automotiveSourceKey || s.vehicleSourceKey || "—"}</td>
                      <td>{s.readinessStatus}</td>
                      <td>{s.ingestionEnabled ? "enabled" : "disabled"}</td>
                      <td>{s.lastSuccessAt ? new Date(s.lastSuccessAt).toLocaleDateString() : "—"}</td>
                      <td>{s.activeListingCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : null}

          {detail.brandAffiliations?.length ? (
            <AdminTableContainer title="Brand affiliations">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Official</th>
                    <th>New</th>
                    <th>Used</th>
                    <th>CPO</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.brandAffiliations.map((b) => (
                    <tr key={b.id}>
                      <td>{b.brandName}</td>
                      <td>{b.officialDealer ? "Yes" : "—"}</td>
                      <td>{b.newVehicleSales ? "Yes" : "—"}</td>
                      <td>{b.usedVehicleSales ? "Yes" : "—"}</td>
                      <td>{b.certifiedPreowned ? "Yes" : "—"}</td>
                      <td>{b.verificationStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : null}

          {detail.ingestScore ? (
            <section className="panel-card">
              <h3>Ingest score (M14)</h3>
              <p>
                Overall {detail.ingestScore.overallScore} · Tier {detail.ingestScore.recommendedTier} ·{" "}
                {detail.ingestScore.recommendedAction}
              </p>
              {detail.ingestScore.primaryReason ? (
                <p className="muted">{detail.ingestScore.primaryReason}</p>
              ) : null}
            </section>
          ) : null}

          {canManage ? (
            <section className="panel-card">
              <h3>Notes</h3>
              <textarea
                rows={3}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                style={{ width: "100%" }}
              />
              <button
                type="button"
                className="admin-btn admin-btn--secondary admin-btn--compact"
                style={{ marginTop: "0.5rem" }}
                onClick={() => patchStatus({ notes: noteDraft })}
              >
                Save note
              </button>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default DealerDetailAdminPage;
